/**
 * Stable backend `errorCode` values for the conversion endpoints
 * (`GET …/conversions/{id}/exists` and `POST …/retrieve-mbrs-data`).
 *
 * They are public contract, identical in every environment: key UI behaviour on
 * these, never on the `errors` prose. Hand-over and reply:
 * `be-conversion-exists-endpoint-20261014*.md` and
 * `be-mbrs-data-not-found-errorcode-20261014*.md` (local, git-ignored).
 */

/** No such usable conversion: missing, soft-deleted, or still awaiting its files. HTTP 404. */
export const CONVERSION_NOT_FOUND_CODE = 'CONVERSION_NOT_FOUND';

/** A real, finished conversion that belongs to someone else. HTTP 403. */
export const CONVERSION_FORBIDDEN_CODE = 'CONVERSION_FORBIDDEN';

/**
 * `retrieve-mbrs-data` only. The conversion is `InProgress` and has not written
 * its data rows yet. NORMAL for a short while after submit and during a Phase-2
 * re-run — not a fault. HTTP 409.
 */
export const MBRS_DATA_NOT_READY_CODE = 'MBRS_DATA_NOT_READY';

/**
 * `retrieve-mbrs-data` only. A usable conversion that is NOT in progress but has
 * no MBRS data rows at all (a `Failed` run lands here too). This is the fault
 * case — the only one the server logs at Error. HTTP 404.
 */
export const MBRS_DATA_NOT_FOUND_CODE = 'MBRS_DATA_NOT_FOUND';

/** `retrieve-mbrs-data` only. Rows exist, but none for the requested `sheet`. HTTP 404. */
export const SHEET_DATA_NOT_FOUND_CODE = 'SHEET_DATA_NOT_FOUND';

/**
 * The `retrieve-mbrs-data` codes that mean "the conversion EXISTS but its data
 * cannot be served". They must never be read as "no such conversion": the
 * status alone (404) would say so, which is why the code takes precedence.
 */
export const CONVERSION_DATA_UNAVAILABLE_CODES: ReadonlySet<string> = new Set([
  MBRS_DATA_NOT_READY_CODE,
  MBRS_DATA_NOT_FOUND_CODE,
  SHEET_DATA_NOT_FOUND_CODE,
]);

/**
 * Any non-User-Management endpoint called by a SuperAdmin. HTTP 403. It means a
 * screen is wrongly reachable for that role, so the UI redirects to User
 * Management rather than reporting a failed load.
 */
export const SUPERADMIN_MODULE_FORBIDDEN_CODE = 'SUPERADMIN_MODULE_FORBIDDEN';

/**
 * User-facing copy for a failed conversion ACTION (download, submit for
 * validation, delete/archive) whose backend `errorCode` says the conversion is
 * missing or belongs to someone else. Defined once so every action shows the
 * same sentence.
 *
 * Page-LOAD paths (listing, retrieve, exists) do NOT use this: they keep going
 * through `buildPageLoadFailureNotice` and its `refactoredDiagnosticCodesEnabled`
 * flag. Read it through `conversionActionFailureCopy(code) ?? genericSentence`
 * (own keys only, so a code such as "constructor" cannot match an inherited member).
 */
const CONVERSION_ACTION_FAILURE_COPY: Record<string, string> = {
  [CONVERSION_FORBIDDEN_CODE]: "You don't have access to this conversion.",
  [CONVERSION_NOT_FOUND_CODE]: 'This conversion no longer exists.',
};

/**
 * Own-property lookup into `CONVERSION_ACTION_FAILURE_COPY`. A plain-object
 * index would answer an inherited member for a code like "constructor" or
 * "toString"; `Object.hasOwn` ignores the prototype chain.
 */
export function conversionActionFailureCopy(errorCode: string | undefined): string | undefined {
  if (errorCode === undefined) return undefined;
  return Object.hasOwn(CONVERSION_ACTION_FAILURE_COPY, errorCode)
    ? CONVERSION_ACTION_FAILURE_COPY[errorCode]
    : undefined;
}
